import React, { useState } from 'react';
import moment from 'moment-timezone';
import useLoading from 'library/hooks/useLoading';
import { Checkbox, ErrorText, FieldGroup, Modal, PillButton, Row, Stack, Text, TimePicker } from 'library/ui';
import { formatShortWeekdayMonthDay } from 'shared/Dates';
import { MINIMUM_SHIFT_MINUTES } from 'parent-portal/jobs/postJob/hooks/useScheduleFields';

import { createCenterInitiatedAppointmentTimeChangeRequest, updateBusinessAppointment } from './businessAppointmentApi';

import type { IShift } from 'models';
import type { ILeanShift } from '../../BusinessJob/models';

function timeToMinutes(time: string) {
    const [hours = 0, minutes = 0] = time.split(':').map(Number);
    return hours * 60 + minutes;
}

function normalizeTime(time: string) {
    return time.slice(0, 5);
}

export default function EditModal({
    open,
    onClose,
    shift,
    providerName,
    refresh,
    timezone,
}: {
    open: boolean;
    onClose: () => void;
    shift: IShift | ILeanShift;
    providerName?: string;
    refresh: () => void;
    timezone: string;
}) {
    const { loading, setLoading } = useLoading();
    const toShiftMoment = (date: string) => (timezone ? moment.tz(date, timezone) : moment(date));
    const withTime = (date: string, time: string) => {
        const [hour = 0, minute = 0] = time.split(':').map(Number);
        return toShiftMoment(date).set({ hour, minute, second: 0, millisecond: 0 });
    };

    const originalStartTime = toShiftMoment(shift.start_date).format('HH:mm');
    const originalEndTime = toShiftMoment(shift.end_date).format('HH:mm');
    const [startTime, setStartTime] = useState(originalStartTime);
    const [endTime, setEndTime] = useState(originalEndTime);
    const [removeProvider, setRemoveProvider] = useState(false);

    const startInPast =
        normalizeTime(startTime) !== originalStartTime && !withTime(shift.start_date, startTime).isAfter(moment());
    const endBeforeStart = timeToMinutes(endTime) < timeToMinutes(startTime);
    const tooShort = !endBeforeStart && timeToMinutes(endTime) - timeToMinutes(startTime) < MINIMUM_SHIFT_MINUTES;

    function submit() {
        if (normalizeTime(startTime) === originalStartTime && normalizeTime(endTime) === originalEndTime) {
            onClose();
            return;
        }
        setLoading(true);
        const newStart = withTime(shift.start_date, startTime);
        const newEnd = withTime(shift.end_date, endTime);

        if (!providerName) {
            updateBusinessAppointment(shift.id, {
                start_date: newStart.toISOString(),
                end_date: newEnd.toISOString(),
            })
                .then(refresh)
                .finally(() => {
                    setLoading(false);
                    onClose();
                });
        } else {
            createCenterInitiatedAppointmentTimeChangeRequest({
                appointmentId: shift.id,
                startTime: newStart,
                endTime: newEnd,
                onDecline: removeProvider ? 'remove_provider' : 'keep_provider',
            })
                .then(refresh)
                .finally(() => {
                    setLoading(false);
                    onClose();
                });
        }
    }

    const mainText = providerName
        ? [
              `We will inform ${providerName} of updates to the shift times.`,
              'If there are any issues with this reschedule, we will update you as soon as possible.',
          ].join(' ')
        : [
              'We will attempt to fill this shift with the updated times.',
              'If you requested to book multiple providers for this shift, all unfilled shifts will be updated.',
          ].join(' ');

    const disclaimerText = providerName
        ? [
              `Note: if ${providerName} does not respond by the shift start time,`,
              `the times will be kept as the original shift times and ${providerName} will remain on the shift.`,
          ].join(' ')
        : false;

    if (!open) {
        return null;
    }

    return (
        <Modal
            title={`Need to update the times for ${formatShortWeekdayMonthDay(shift.start_date)}?`}
            onClose={onClose}
        >
            <Stack gap="md">
                <Text>{mainText}</Text>
                {disclaimerText && <Text>{disclaimerText}</Text>}
                <Row gap="md" wrap align="start">
                    <FieldGroup label="Start">
                        <TimePicker value={startTime} onChange={setStartTime} />
                        {startInPast && <ErrorText>Start time cannot be in the past</ErrorText>}
                    </FieldGroup>
                    <FieldGroup label="End">
                        <TimePicker value={endTime} onChange={setEndTime} />
                        {endBeforeStart && <ErrorText>End time must be after start time</ErrorText>}
                        {tooShort && <ErrorText>Duration must be at least {MINIMUM_SHIFT_MINUTES} minutes</ErrorText>}
                    </FieldGroup>
                </Row>
                {!!providerName && (
                    <Checkbox
                        label={`Remove ${providerName} if they cannot work the new times.`}
                        checked={removeProvider}
                        onChange={setRemoveProvider}
                    />
                )}
                <Row justify="end" fullWidth>
                    <PillButton disabled={loading || startInPast || endBeforeStart || tooShort} onClick={submit}>
                        {loading ? 'Submitting…' : 'Submit'}
                    </PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}
